import { useGetFilter } from 'hooks/useSearchParamsObj';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from '@redux';
import { getTransferOrderStatusCount } from '@redux/config/actions';
import styled from 'styled-components';
import { getColumns } from './columns';
import { IRestColumnProps } from 'components/rest/Table/TableColumn';
import RestList from 'containers/rest/List';
import { useNavigate } from 'react-router-dom';
import { TLayoutItem } from '@redux/layouts/actions';
import useGetSearchKey from 'hooks/useSearchKeys';
import { DATA_FIELD_SEARCH } from './utilts';
import { getListProps } from './list';
import ButtonCreateBatch from '../components/ButtonCreateBatch';
import crudActions from '@redux/crudActions';
import { useTranslation } from 'react-i18next';
import { DynamicObjectType } from 'core/types/common';
import sumBy from 'lodash/sumBy';
import { TRANSFER_TABS } from 'constants/transferOrders';
import { getFilters } from './filters';
import { TFilterItem } from 'components/rest/Filter/FilterRow';
import { newCheckRole } from 'utils/tools';

const List = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const listReft = useRef(null);
  const navigate = useNavigate();
  const { searchParams, filter } = useGetFilter();
  const { searchKeys } = useGetSearchKey(DATA_FIELD_SEARCH);

  const statusTOCount = useSelector<DynamicObjectType[]>(
    (state) => state.config.statusTOCount,
  );
  const user = useSelector<DynamicObjectType>((state) => state.auth.data);

  useEffect(() => {
    dispatch(getTransferOrderStatusCount({}));
  }, [searchParams.get('customLayout')]);

  const handleNameTab = useCallback(
    (e) => {
      const data = TRANSFER_TABS.find((item) => item.subValue.includes(e));
      if (e !== 'all') {
        return (
          <div className="flex items-center">
            <div>{t(data?.text)}</div>
            <div className="rounded-[5px] px-1 py-0.5 ml-1 text-center min-w-10 count text-xs">{` ${
              sumBy(
                statusTOCount?.filter(
                  (item: DynamicObjectType) => item?.transferStatus === e,
                ),
                (o) => Number(o?.count),
              ) || 0
            }`}</div>
          </div>
        );
      }
      return t(data?.text || e);
    },
    [statusTOCount, t],
  );

  const INITIAL_LAYOUTS: TLayoutItem[] = useMemo(() => {
    return [
      {
        rootPath: '/transfer-orders',
        pathId: 'AWAITING_CONFIRMATION',
        title: 'Awaiting Confirmation',
        label: handleNameTab('AWAITING_CONFIRMATION'),
        customPath: `/transfer-orders?customLayout=AWAITING_CONFIRMATION`,
        isDefault: true,
      },
      {
        rootPath: '/transfer-orders',
        pathId: 'CONFIRMED_PICK',
        title: 'Confirmed Pick',
        label: handleNameTab('CONFIRMED_PICK'),
        customPath: `/transfer-orders?customLayout=CONFIRMED_PICK`,
        isDefault: true,
      },
      {
        rootPath: '/transfer-orders',
        pathId: 'SHIPPING',
        title: 'Shipping',
        label: handleNameTab('SHIPPING'),
        customPath: `/transfer-orders?customLayout=SHIPPING`,
        isDefault: true,
      },
      // {
      //   rootPath: '/transfer-orders',
      //   pathId: 'RECEIVED',
      //   title: 'Received',
      //   customPath: `/transfer-orders?customLayout=RECEIVED&filters=${encodeURIComponent(
      //     JSON.stringify([FILTERS_OBJECT['RECEIVED']], null, 0),
      //   )}`,
      //   isDefault: true,
      // },
      {
        rootPath: '/transfer-orders',
        pathId: 'REJECTED',
        title: 'Rejected',
        label: handleNameTab('REJECTED'),
        customPath: `/transfer-orders?customLayout=REJECTED`,
        isDefault: true,
      },
      {
        rootPath: '/transfer-orders',
        pathId: 'COMPLETED',
        title: 'Completed',
        label: handleNameTab('COMPLETED'),
        customPath: `/transfer-orders?customLayout=COMPLETED`,
        isDefault: true,
      },
      {
        rootPath: '/transfer-orders',
        pathId: 'CANCELLED',
        title: 'Cancelled',
        label: handleNameTab('CANCELLED'),
        customPath: `/transfer-orders?customLayout=CANCELLED`,
        isDefault: true,
      },
    ];
  }, [handleNameTab]);

  const _columns = useMemo(() => {
    return getColumns(searchParams.get('customLayout')) as IRestColumnProps[];
  }, [searchParams.get('customLayout')]);

  const _listProps = useMemo(() => {
    return getListProps(
      searchParams.get('customLayout'),
      searchParams,
      filter,
      user?.roles,
      user?.isSuperUser,
    );
  }, [
    searchParams.get('customLayout'),
    filter,
    user?.roles,
    user?.isSuperUser,
  ]);

  const _filters: TFilterItem[] = useMemo(() => {
    return getFilters(searchParams.get('customLayout'));
  }, [searchParams.get('customLayout')]);

  useEffect(() => {
    if (searchParams.get('customLayout') === 'CONFIRMED_PICK') {
      dispatch(crudActions.transferConfirmedPick.setSelectedRowKeys([] as any));
    }
  }, []);

  useEffect(() => {
    dispatch(crudActions['transferOrdersBatch'].clearCurrent());
    dispatch(crudActions['transferOrders'].clearCurrent());
  }, [window.location.pathname]);

  const hasCardComponent = useMemo(() => {
    return (
      searchParams.get('customLayout') === 'CONFIRMED_PICK' &&
      newCheckRole(
        '/transfer-orders-batch/create',
        'POST',
        user?.roles,
        user?.isSuperUser,
      ) && <ButtonCreateBatch listReft={listReft} />
    );
  }, [
    user?.roles,
    user?.isSuperUser,
    searchParams.get('customLayout'),
    listReft,
  ]);

  return (
    <TOListWrapper>
      <RestList
        columns={_columns}
        gotoCreatePage={() => navigate('/transfer-orders/create')}
        searchKeys={searchKeys}
        placeholderInputSearch="Search Order ID, Transfer ID, TO number, SKU..."
        dataFieldSearch={DATA_FIELD_SEARCH}
        hasCustomLayout
        initialLayouts={INITIAL_LAYOUTS}
        filters={_filters}
        {..._listProps}
        hasCardComponent={hasCardComponent}
        listReft={listReft}
      />
    </TOListWrapper>
  );
};

const TOListWrapper = styled.div`
  .ant-tabs-tab-active .ant-tabs-tab-btn {
    .count {
      background: var(--light-primary);
    }
  }
  .ant-tabs-tab-btn {
    .count {
      background: #2c35461a;
    }
  }
`;

export default List;
